import React, { useEffect, useMemo, useState } from "react";
import { Plus, Check, X, Link2, Copy, CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { leaveApi, employeesApi } from "../lib/api";
import { LEAVE_TYPES } from "../mock";
import { useToast } from "../hooks/use-toast";

const statusStyle = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

function PortalLinkCard({ code, orgName }) {
  const { toast } = useToast();
  const link = `${window.location.origin}/portal/${code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast({ title: "Link copied", description: "Share it with your team to request leave." }); };
  return (
    <div className="bg-gradient-to-br from-brand-navy to-brand-blue rounded-2xl p-5 text-white flex flex-wrap items-center gap-4 justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Link2 className="w-5 h-5" /></div>
        <div>
          <div className="font-display font-bold">Employee leave request link</div>
          <div className="text-sm text-brand-skysoft">Employees at {orgName} can request leave & clock in here — no admin login needed.</div>
        </div>
      </div>
      <div className="flex items-center gap-2 bg-white/10 rounded-lg pl-3 pr-1 h-11">
        <span className="text-sm text-white/90 max-w-[280px] truncate">{link}</span>
        <button onClick={copy} className="h-9 px-3 rounded-md bg-white text-brand-blue text-sm font-semibold flex items-center gap-1.5 hover:bg-brand-light transition-colors"><Copy className="w-4 h-4" /> Copy</button>
      </div>
    </div>
  );
}

function RequestDialog({ open, onOpenChange, employees, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ employee_id: "", leave_type: "Annual", start_date: "", end_date: "", reason: "" });
  useEffect(() => { if (open) setForm({ employee_id: "", leave_type: "Annual", start_date: "", end_date: "", reason: "" }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast({ title: "Select an employee", variant: "destructive" });
    try { await leaveApi.create(form); onSaved(); onOpenChange(false); toast({ title: "Leave request created" }); }
    catch (err) { toast({ title: "Could not create", description: err?.response?.data?.detail || "Try again", variant: "destructive" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-brand-navy">New leave request</DialogTitle><DialogDescription>Log leave on behalf of an employee.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_number}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Leave type</Label>
            <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-500">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></div>
          </div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional" /></div>
          <Button type="submit" className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">Create request</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Leave() {
  const { session } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    try {
      const [r, b] = await Promise.all([leaveApi.list(), leaveApi.balances()]);
      setRequests(r); setBalances(b);
    } catch (e) {}
  };
  useEffect(() => { load(); employeesApi.list().then(setEmployees).catch(() => {}); }, []);

  const decide = async (id, status) => {
    try { await leaveApi.decide(id, status); await load(); toast({ title: `Leave ${status}` }); }
    catch (e) { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const filtered = useMemo(() => requests.filter((r) => filter === "all" ? true : r.status === filter), [requests, filter]);
  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="pt-2 pb-6 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-navy">Leave</h1>
          <p className="text-slate-500 mt-1">{pendingCount} pending {pendingCount === 1 ? "request" : "requests"} · balances follow the Act's entitlements</p>
        </div>
        <Button onClick={() => setDialog(true)} className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11 px-5"><Plus className="w-4 h-4 mr-1" /> New Request</Button>
      </div>

      <PortalLinkCard code={session?.organization?.portal_code} orgName={session?.organization?.name} />

      <Tabs defaultValue="requests">
        <TabsList className="mb-4">
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="balances">Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <div className="flex gap-2 mb-4">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 h-9 rounded-lg text-sm font-medium capitalize transition-colors ${filter === f ? "bg-brand-blue text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-brand-blue"}`}>{f}</button>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-5 font-medium">Employee</th>
                <th className="py-3 px-3 font-medium">Type</th>
                <th className="py-3 px-3 font-medium">Dates</th>
                <th className="py-3 px-3 font-medium">Days</th>
                <th className="py-3 px-3 font-medium">Source</th>
                <th className="py-3 px-3 font-medium">Status</th>
                <th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 last:border-0 hover:bg-brand-mist/40">
                    <td className="py-3 px-5"><div className="font-semibold text-brand-navy">{r.employee_name}</div><div className="text-xs text-slate-500">{r.department}</div></td>
                    <td className="py-3 px-3 text-slate-600">{r.leave_type}</td>
                    <td className="py-3 px-3 text-slate-600 whitespace-nowrap">{r.start_date} → {r.end_date}</td>
                    <td className="py-3 px-3 text-slate-600">{r.days}</td>
                    <td className="py-3 px-3"><span className="text-xs text-slate-400 capitalize">{r.source}</span></td>
                    <td className="py-3 px-3"><Badge className={`${statusStyle[r.status]} hover:bg-current/10 capitalize`}>{r.status}</Badge></td>
                    <td className="py-3 px-5">
                      {r.status === "pending" ? (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => decide(r.id, "approved")} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"><Check className="w-4 h-4" /> Approve</button>
                          <button onClick={() => decide(r.id, "rejected")} className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600"><X className="w-4 h-4" /> Reject</button>
                        </div>
                      ) : <span className="text-xs text-slate-400">{r.decided_by ? `by ${r.decided_by}` : "—"}</span>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400"><CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-300" />No {filter !== "all" ? filter : ""} requests.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="balances">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-5 font-medium">Employee</th>
                <th className="py-3 px-3 font-medium">Annual (rem/ent)</th>
                <th className="py-3 px-3 font-medium">Sick (rem/ent)</th>
                <th className="py-3 px-3 font-medium">Other taken</th>
              </tr></thead>
              <tbody>
                {balances.map((b) => {
                  const annual = b.balances.find((x) => x.type === "Annual") || {};
                  const sick = b.balances.find((x) => x.type === "Sick") || {};
                  const other = b.balances.filter((x) => !["Annual", "Sick"].includes(x.type)).reduce((s, x) => s + (x.used || 0), 0);
                  return (
                    <tr key={b.employee_id} className="border-b border-slate-50 last:border-0 hover:bg-brand-mist/40">
                      <td className="py-3 px-5"><div className="font-semibold text-brand-navy">{b.name}</div><div className="text-xs text-slate-500">{b.department}</div></td>
                      <td className="py-3 px-3"><span className="font-semibold text-brand-navy">{annual.remaining ?? "—"}</span><span className="text-slate-400"> / {annual.entitlement ?? "—"}</span></td>
                      <td className="py-3 px-3"><span className="font-semibold text-brand-navy">{sick.remaining ?? "—"}</span><span className="text-slate-400"> / {sick.entitlement ?? "—"}</span></td>
                      <td className="py-3 px-3 text-slate-600">{other} days</td>
                    </tr>
                  );
                })}
                {balances.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-slate-400">No employees.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <RequestDialog open={dialog} onOpenChange={setDialog} employees={employees} onSaved={load} />
    </div>
  );
}
